import { useState, useEffect } from 'react';
import { CourseOption } from '../../types/course.response';
import PlanSidebar from './PlanSidebar';
import SemestersGridView from './SemestersGrid';
import { userCoursesApi } from '../../services/api/user-courses.api';
import { getCourseId, getCourseTopic, getCourseCredit, getCourseKey, getCourseIsSaved, getUserItemId } from '../../utils/courseUtils';
import { autoAssignSections } from '../../utils/scheduleUtils';
import Loading from '../../components/loading/Loading';
import { DEFAULT_SEMESTER } from '../../constants';
import { useAuth } from '../../contexts/AuthContext';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import type { DragSource } from './CourseCardForDragging';

export default function Plan() {
  const { user } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [selectedSemester, setSelectedSemester] = useState<string>(DEFAULT_SEMESTER);
  const [userCourses, setUserCourses] = useState<CourseOption[]>([]);
  const [activeDragCourse, setActiveDragCourse] = useState<CourseOption | null>(null);

  const fetchUserCourses = async () => {
    setLoading(true);
    setError(undefined);
    try {
      const courses = await userCoursesApi.getCourses();
      setUserCourses(courses);
    } catch (err) {
      console.error('Error fetching user courses:', err);
      setError('Error fetching user courses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserCourses();
  }, []);

  const handleSaveCourse = async (courseOption: CourseOption) => {
    setLoading(true);
    setError(undefined);
    try {
      const courseId = getCourseId(courseOption);
      const topic = getCourseTopic(courseOption);
      const creditsReceived = getCourseCredit(courseOption);
      const { data: added } = await userCoursesApi.addCourse(
        courseId,
        topic,
        false, // isScheduled = false for saved
        true, // isSaved = true for saved
        null,
        creditsReceived,
        [],
        []
      );
      setUserCourses(prev => [...prev, added]);
    } catch (err) {
      console.error('Error saving course:', err);
      setError(err instanceof Error ? err.message : 'Failed to save course');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCourseToSemester = async (courseOption: CourseOption, semester: string) => {
    setLoading(true);
    setError(undefined);
    try {
      const courseId = getCourseId(courseOption);
      const topic = getCourseTopic(courseOption);
      const creditsReceived = getCourseCredit(courseOption);
      const classSectionIds = autoAssignSections(courseOption, semester, userCourses);
      const isSaved = getCourseIsSaved(courseOption);
      const { data: added } = await userCoursesApi.addCourse(
        courseId, topic, true, isSaved, semester, creditsReceived, classSectionIds, []
      );
      setUserCourses(prev => [...prev, added]);
    } catch (err) {
      console.error('Error adding course to semester:', err);
      setError(err instanceof Error ? err.message : 'Failed to add course');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = async (courseOption: CourseOption) => {
    if (courseOption.userItemId === null) return;
    setLoading(true);
    setError(undefined);
    try {
      await userCoursesApi.deleteCourse(courseOption.userItemId);
      setUserCourses(prev =>
        prev.filter(c => getUserItemId(c) !== getUserItemId(courseOption))
      );
    } catch (err) {
      console.error('Error deleting course:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete course');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCourseFromSchedule = async (courseOption: CourseOption) => {
    if (courseOption.userItemId === null) return;
    const previousCourses = userCourses;

    setUserCourses(prev =>
      prev.map(c => getUserItemId(c) === getUserItemId(courseOption)
        ? { ...c, userState: { ...c.userState, semester: '', isScheduled: false, isSaved: true, status: 'SAVED' as const } }
        : c
      )
    );

    try {
      const { data: updated } = await userCoursesApi.updateCourse(courseOption.userItemId, {
        isScheduled: false,
        semester: null,
        isSaved: true,
        creditsReceived: null,
        classSectionIds: [],
      });
      setUserCourses(prev =>
        prev.map(c => getUserItemId(c) === getUserItemId(courseOption) ? updated : c)
      );
    } catch (err) {
      setUserCourses(previousCourses);
      setError(err instanceof Error ? err.message : 'Failed to remove course');
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const dragData = event.active.data.current;
    if (dragData?.courseOption) {
      setActiveDragCourse(dragData.courseOption as CourseOption);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragCourse(null);

    if (!over) return;

    const dragData = active.data.current as { courseOption: CourseOption; source: DragSource } | undefined;
    const dropData = over.data.current as { type: string; semester?: string } | undefined;

    if (!dragData?.courseOption || !dropData?.type) return;

    const course = dragData.courseOption;
    const source = dragData.source;

    if (dropData.type === 'semester' && dropData.semester) {
      const targetSemester = dropData.semester;

      if (source === 'semester' && course.userState.semester === targetSemester) return;

      if (source === 'sidebar-saved' || source === 'sidebar-unassigned') {
        handleMoveCourseToSemester(course, targetSemester);
      } else if (source === 'semester') {
        handleMoveCourseToSemester(course, targetSemester);
      }
    } else if (dropData.type === 'sidebar-saved' && source === 'semester') {
      handleRemoveCourseFromSchedule(course);
    }
  };

  const handleMoveCourseToSemester = async (courseOption: CourseOption, semester: string) => {
    if (courseOption.userItemId === null) return;
    const previousCourses = userCourses;
    const classSectionIds = autoAssignSections(courseOption, semester, userCourses);
    const creditsReceived = getCourseCredit(courseOption);

    setUserCourses(prev =>
      prev.map(c => getUserItemId(c) === getUserItemId(courseOption)
        ? { ...c, userState: { ...c.userState, semester, isScheduled: true, status: 'PLANNED' as const } }
        : c
      )
    );

    try {
      const { data: updated } = await userCoursesApi.updateCourse(courseOption.userItemId, {
        isScheduled: true,
        semester,
        classSectionIds,
        creditsReceived,
      });
      setUserCourses(prev =>
        prev.map(c => getUserItemId(c) === getUserItemId(courseOption) ? updated : c)
      );
    } catch (err) {
      setUserCourses(previousCourses);
      setError(err instanceof Error ? err.message : 'Failed to move course');
    }
  };

  const handleDragCancel = () => {
    setActiveDragCourse(null);
  };
  
  return (
    <>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50">
          <Loading size="lg" />
        </div>
      )}
      {error && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[1002] bg-red-50 border border-red-100 rounded px-4 py-3 shadow-md min-w-80">
          <p className="text-red-700 font-medium text-sm">{error}</p>
          <button className="mt-1 text-xs text-red-700 underline" onClick={() => setError(undefined)}>Dismiss</button>
        </div>
      )}
      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
        <div className="flex pt-6 gap-1 max-w-[1200px] w-full mx-auto h-[calc(100vh-4rem)] overflow-hidden">
          {/* Semesters Grid (left, main area) */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden h-full">
            <div className="pt-3 pb-6 px-4">
              <SemestersGridView
                semesters={user?.semesters || []}
                userCourses={userCourses}
                selectedSemester={selectedSemester}
                onSemesterSelect={setSelectedSemester}
                onAddCourse={handleAddCourseToSemester}
                onDeleteCourse={handleDeleteCourse}
              />
            </div>
          </div>
          {/* Right Sidebar */}
          <div className="hidden md:block w-[350px] px-5 overflow-hidden h-full">
            <PlanSidebar
              userCourses={userCourses}
              onSaveCourse={handleSaveCourse}
              onDeleteCourse={handleDeleteCourse}
            />
          </div>
        </div>
        <DragOverlay>
          {activeDragCourse ? (
            <div className="bg-white w-64 border-2 border-blue-500 rounded-md shadow-lg px-3 py-2 cursor-grabbing">
              <p className="text-xs text-gray-600">
                {activeDragCourse.courseInfo.subject} {activeDragCourse.courseInfo.number}
              </p>
              <p className="text-sm font-medium truncate">
                {activeDragCourse.courseInfo.titleShort}
              </p>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
}
